import * as React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';

import useAxios from '../utils/useAxios';
import { ConfirmDialog, SectionCard } from './ui';
import { TRUE_COLORS } from '../theme';

/** A provider's connection as Receipt-Web-Server integrations/providers.py lists it. */
type Account = {
  account: string; name: string; connect: 'oauth' | 'keys'; available: boolean; connected: boolean;
  kinds: ('online' | 'terminal')[]; providers?: { key: string; kind: 'online' | 'terminal' }[];
  fields: { name: string; label: string; secret?: boolean; help?: string; required?: boolean }[];
  details: Record<string, string>;
};
type Online = { provider: string | null; name: string | null; choices: { key: string; name: string; connected: boolean }[] };
type Terminal = { id: string; serial: string; model: string; checkout: number | null };
type Register = { id: number; label: string };

const KIND_LABEL = { online: 'Pagos en línea', terminal: 'Terminales' };

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.response === 'string') return data.response;
  return fallback;
};

/** A provider connected with keys: the form to connect it, or its account and terminals. */
const KeysProvider = ({ account, onChange }: { account: Account; onChange: (data: { accounts: Account[]; online: Online }) => void }) => {
  const axios = useAxios();
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [confirm, setConfirm] = React.useState(false);
  const terminalKey = account.providers?.find(p => p.kind === 'terminal')?.key;
  const [terminals, setTerminals] = React.useState<Terminal[] | null>(null);
  const [registers, setRegisters] = React.useState<Register[]>([]);

  React.useEffect(() => {
    if (!account.connected || !terminalKey) return;
    axios.get(`api/integrations/providers/${terminalKey}/terminals/`)
      .then(({ data }) => { setTerminals(data.terminals); setRegisters(data.checkouts); })
      .catch(err => { setTerminals([]); setError(errorText(err, 'No se pudieron cargar las terminales.')); });
  }, [axios, account.connected, terminalKey]);

  const connect = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`api/integrations/providers/${account.account}/connect/`, { credentials: values });
      setValues({});
      onChange(data);
    } catch (err) {
      setError(errorText(err, `No se pudo conectar ${account.name}.`));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    try {
      const { data } = await axios.delete(`api/integrations/providers/${account.account}/connect/`);
      onChange(data);
    } catch (err) {
      setError(errorText(err, `No se pudo desconectar ${account.name}.`));
    } finally {
      setConfirm(false);
    }
  };

  const assign = async (terminal: Terminal, value: string) => {
    try {
      const { data } = await axios.post(`api/integrations/providers/${terminalKey}/terminals/`, { terminal: terminal.id, checkout: value === '' ? null : Number(value) });
      setTerminals(data.terminals);
    } catch (err) {
      setError(errorText(err, 'No se pudo cambiar la terminal.'));
    }
  };

  return (
    <Box data-testid={`provider-${account.account}`} sx={{ p: 2, borderRadius: 2, border: `1px solid ${TRUE_COLORS.borderSolid}` }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.5 }}>
        <Typography sx={{ fontWeight: 700 }}>{account.name}</Typography>
        {account.kinds.map(kind => <Chip key={kind} size="small" variant="outlined" label={KIND_LABEL[kind]} />)}
        {account.connected && <Chip size="small" color="success" label="Conectado" />}
      </Stack>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mt: 1.5 }}>{error}</Alert>}
      {!account.connected ? (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {account.fields.map(field => (
            <TextField key={field.name} size="small" label={field.label} type={field.secret ? 'password' : 'text'} autoComplete="off"
              value={values[field.name] ?? ''} onChange={e => setValues(v => ({ ...v, [field.name]: e.target.value }))} helperText={field.help} />
          ))}
          <Box><Button variant="contained" onClick={connect} disabled={busy}>Conectar {account.name}</Button></Box>
        </Stack>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 1.5 }}>
          {Object.entries(account.details).map(([key, value]) => (
            <Typography key={key} variant="body2" sx={{ color: 'text.secondary' }}>{value}</Typography>
          ))}
          {terminals?.map(terminal => (
            <Stack key={terminal.id} direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
              <PointOfSaleOutlinedIcon sx={{ color: TRUE_COLORS.primary }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{terminal.model}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Serie {terminal.serial}</Typography>
              </Box>
              <TextField select size="small" label="Cobra para" value={terminal.checkout ?? ''} sx={{ minWidth: 220 }} onChange={e => assign(terminal, e.target.value)}>
                <MenuItem value="">Ninguna caja</MenuItem>
                {registers.map(register => <MenuItem key={register.id} value={register.id}>{register.label}</MenuItem>)}
              </TextField>
            </Stack>
          ))}
          <Box><Button color="error" size="small" onClick={() => setConfirm(true)}>Desconectar {account.name}</Button></Box>
        </Stack>
      )}
      <ConfirmDialog open={confirm} danger title={`¿Desconectar ${account.name}?`} confirmLabel="Desconectar"
        description="TRUE dejará de cobrar con esta cuenta. Puedes conectarla de nuevo cuando quieras."
        onClose={() => setConfirm(false)} onConfirm={disconnect} />
    </Box>
  );
};

/**
 * Other payment providers in Configuración (any besides Mercado Pago, which has its own card): connect them with their
 * keys, assign their terminals, and choose who takes the business's online payments when more than one can. Shows
 * nothing while there's nothing to choose (only Mercado Pago). Administrators only.
 */
const PaymentProviders = () => {
  const axios = useAxios();
  const [data, setData] = React.useState<{ accounts: Account[]; online: Online } | null>(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    axios.get('api/integrations/providers/').then(({ data }) => setData(data)).catch(() => setData(null));
  }, [axios]);

  if (!data) return null;
  const others = data.accounts.filter(a => a.connect === 'keys' && a.available);
  const connectedOnline = data.online.choices.filter(choice => choice.connected);
  if (others.length === 0 && connectedOnline.length < 2) return null;

  const choose = async (key: string) => {
    setError('');
    try {
      const { data: next } = await axios.put('api/integrations/providers/', { online_provider: key });
      setData(next);
    } catch (err) {
      setError(errorText(err, 'No se pudo cambiar el proveedor.'));
    }
  };

  return (
    <SectionCard title="Otros proveedores de pago" subtitle="Cobra con la cuenta que tu negocio ya usa: en línea o con sus terminales">
      <Stack spacing={2}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {connectedOnline.length > 0 && (
          <TextField select size="small" label="Tus clientes pagan en línea con" value={data.online.provider ?? ''} sx={{ maxWidth: 360 }}
            onChange={e => choose(e.target.value)} data-testid="online-provider"
            helperText="Pedidos pagados al ordenar y recargas de saldo desde la tarjeta del cliente.">
            {connectedOnline.map(choice => <MenuItem key={choice.key} value={choice.key}>{choice.name}</MenuItem>)}
          </TextField>
        )}
        {others.map(account => <KeysProvider key={account.account} account={account} onChange={setData} />)}
      </Stack>
    </SectionCard>
  );
};

export default PaymentProviders;
